import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import type { Lead } from 'services/leadApi'
import { date, datetime, relativeOrDate } from 'utils'
import { exportRowsToCsv } from 'utils/exportActivity'

import { useLeadsQuery, useOwnersQuery } from 'hooks/api'
import { useCanAccess } from 'hooks'
import { TRANSFER_PERMISSION_TOOLTIP } from 'utils'
import { useSnackbar } from 'contexts'
import { ArrowLeftRightIcon, CopyIcon, DownloadIcon, UserPlusIcon, UsersRoundIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Stack, Tooltip, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import {
  ActionDropdown,
  CertificateCell,
  EmptyState,
  SelectField,
  Table,
  TransferCertificateModal,
  TwoLineCell,
  UserBlock,
  UserGroupCell,
  type ActionDropdownItem,
} from 'components'

type Row = Lead

// VRG-161: Leads now show an avatar (reverses the earlier VRG-105 avatar-less block).
// A lead whose email matched an existing account shows their photo/initials + name; an
// anonymous / unmatched lead shows the generic person icon in a circle. UserBlock owns
// that photo→initials→person-icon logic, so the Leads name cell, the scan row, and the
// map all read as one system.
const LeadNameCell = ({ row }: { row: Row }) => (
  <Stack sx={{ minWidth: 0, py: 2 }} justifyContent="center" height="100%">
    <UserBlock
      user={{ name: row.name, email: row.email, avatar: (row as any).avatar }}
      size={40}
    />
  </Stack>
)

// Exposes an imperative `exportCsv` handle so PeoplePage's shared Header can host
// the "Export" button (only visible on the Leads tab) while LeadsTab keeps
// ownership of the fetched leads / current search+filter state.
export type LeadsTabHandle = {
  exportCsv: () => void
}

type RegisteredFilter = 'all' | 'yes' | 'no'

const REGISTERED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Registered' },
  { value: 'no', label: 'Not registered' },
]

export const LeadsTab = forwardRef<LeadsTabHandle>((_props, ref) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { open: notification } = useSnackbar()
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 })
  const [search, setSearch] = useState('')
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  // The lead whose transfer modal is open, or null. Holding the ROW (not an id) keeps
  // the email and the scanned ids together — the two things the modal needs.
  const [transferLead, setTransferLead] = useState<Row | null>(null)
  const { isHasAccess: isCanTransfer } = useCanAccess({ action: 'transfer_certificate' })

  // Owners tab "Leads" count → Leads tab deep link (?tab=leads&referrer=<ownerId>) —
  // mirrors how CertificatesPage seeds `productId` from the Table's `?filters=` param.
  // Committed referrer/registered filters are applied via the Filters drawer's Apply
  // button (draft state below), same gate as the native Last Scanned date filter.
  const [referrerFilter, setReferrerFilter] = useState<string | undefined>(
    () => searchParams.get('referrer') || undefined,
  )
  const [referrerDraft, setReferrerDraft] = useState<string | undefined>(referrerFilter)
  const [registered, setRegistered] = useState<RegisteredFilter>('all')
  const [registeredDraft, setRegisteredDraft] = useState<RegisteredFilter>('all')
  const [lastScannedFrom, setLastScannedFrom] = useState<string | undefined>()
  const [lastScannedTo, setLastScannedTo] = useState<string | undefined>()

  // Lightweight owners fetch (react-query, not the redux owners store) to populate
  // the Referrer filter's options — mirrors useLeadsQuery's "lighter-weight fetch
  // pattern" comment. Capped at 100 — company owner lists are not expected to exceed
  // this in practice; a true searchable server-side picker is out of scope here.
  const { data: ownersData } = useOwnersQuery({ size: 100, sort: 'name', order: 'asc' })
  const ownerOptions = useMemo(
    () => [
      { value: '', label: 'All owners' },
      ...(ownersData?.items || []).map((owner) => ({
        value: owner.id,
        label: owner.name || owner.email,
      })),
    ],
    [ownersData],
  )

  const filters = useMemo(
    () => ({
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
      q: search || undefined,
      sort: 'lastSeen' as const,
      order: 'desc' as const,
      referrer: referrerFilter,
      registered: registered === 'all' ? undefined : registered === 'yes',
      lastScannedFrom,
      lastScannedTo,
    }),
    [paginationModel, search, referrerFilter, registered, lastScannedFrom, lastScannedTo],
  )

  const { data, isLoading, refetch: refetchLeads } = useLeadsQuery(filters)

  const leads = data?.items || []
  const total = data?.totalItems || 0

  const referrerName = (referrerRow: Row['referrers'][number]) =>
    referrerRow.name || `${referrerRow.first_name || ''} ${referrerRow.last_name || ''}`.trim() || referrerRow.email

  const toCsvRow = (lead: Row) => ({
    Email: lead.email,
    Name: lead.name || '',
    // VRG-720: separate columns here, unlike the grid — a CSV is read in a spreadsheet,
    // where filtering by company or dialling a phone number wants its own cell.
    Company: lead.company || '',
    Title: lead.title || '',
    Phone: lead.phone || '',
    Referrers: lead.referrers.map(referrerName).join('; '),
    Scans: String(lead.scans),
    'Last Scanned': lead.lastSeen ? date(lead.lastSeen) || '' : '',
    'Last Scan Location': lead.lastScanLocation || '',
  })

  useImperativeHandle(ref, () => ({
    exportCsv: () => {
      // Exports the currently-fetched leads (respecting the active search/referrer
      // filter) — a client-side export of the loaded page, per VERA-914 scope.
      exportRowsToCsv(leads.map(toCsvRow), 'leads')
    },
  }))

  const handlePaginationModelChange = (newModel: { page: number; pageSize: number }) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({ page: 0, pageSize: newModel.pageSize })
    } else {
      setPaginationModel(newModel)
    }
  }

  // Syncs the native "Last Scanned" (lastSeen, type dateTime) drawer filter to the
  // server-side lastScannedFrom/To params — mirrors CertificatesPage's
  // handleFiltersChange pattern for its product prefilter.
  const handleFiltersChange = (appliedFilters: { field: string; operator?: string; value: unknown }[]) => {
    const lastSeenFilters = appliedFilters.filter((f) => f.field === 'lastSeen')
    const from = lastSeenFilters.find((f) => f.operator === 'onOrAfter')?.value as string | Date | undefined
    const to = lastSeenFilters.find((f) => f.operator === 'onOrBefore')?.value as string | Date | undefined
    setLastScannedFrom(from ? new Date(from).toISOString() : undefined)
    setLastScannedTo(to ? new Date(to).toISOString() : undefined)
  }

  // Commits the extra (Referrer/Registered) filters on Apply — fired by the same
  // click that commits the native Last Scanned date range (Filters.tsx calls
  // onChange then onApply synchronously), so all three land together.
  const handleFiltersApply = () => {
    setReferrerFilter(referrerDraft)
    setRegistered(registeredDraft)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }

  const extraFiltersDirty = referrerDraft !== referrerFilter || registeredDraft !== registered

  const getActionItems = (row: Row): ActionDropdownItem[] => [
    {
      /**
       * VRG-720 — turn a lead into the owner of the thing they scanned, in one step.
       *
       * This is deliberately NOT "add as owner, then go find the certificate". The
       * transfer endpoint takes an EMAIL, not an owner id
       * (`TransferCertificateToOwnerRequestDto.toCustomerEmail`), resolves it through
       * `getOrCreateCustomerByEmail`, and provisions the owner itself when nobody
       * matches — picking the one correct email off `recipientIsNew` (a claim invite
       * for a new person, the ordinary accept notice for an existing one). So
       * creating the owner first is a step the system does not need, and it is
       * precisely the step that throws away the context: you leave the row, land on
       * the Owners list, and now have to find a certificate this row was showing you.
       *
       * The row already knows the candidates, so the modal is handed them and decides:
       * one scanned certificate preselects, several open a picker holding only those,
       * none explains why. "Add as owner" stays below for a lead with nothing to hand
       * over yet.
       */
      label: 'Transfer certificate',
      onClick: () => setTransferLead(row),
      // §46, state half: a lead with no identifiable certificate has nothing this
      // action could act on, so it is hidden rather than shown dead. (A scan can
      // carry an email and no certificate id.)
      visible: row.scannedCertificateIds.length > 0,
      // §46, permission half: visible and disabled, with the permission named, because
      // the person who can grant it is someone else and the tooltip is the only place
      // that is ever said.
      disabled: !isCanTransfer,
      tooltip: !isCanTransfer ? TRANSFER_PERMISSION_TOOLTIP : undefined,
      icon: <ArrowLeftRightIcon strokeWidth={1} />,
    },
    {
      label: 'Add as owner',
      onClick: () => {
        const params = new URLSearchParams({ email: row.email })
        if (row.name) params.set('full_name', row.name)
        navigate(`${ROUTES.OWNER_CREATE}?${params.toString()}`)
      },
      icon: <UserPlusIcon strokeWidth={1} />,
    },
    {
      label: 'Copy email',
      onClick: () => {
        navigator.clipboard.writeText(row.email)
        notification({
          type: 'success',
          text: `Copied ${row.email} to clipboard`,
        })
      },
      icon: <CopyIcon strokeWidth={1} />,
    },
  ]

  // VERA-914 (Stage B) — matches the Owners table's bulk-action shape. "Add as
  // owner" stays row-only (kebab, above): there's no bulk owner-creation endpoint,
  // and looping the single-owner create mutation client-side risks partial
  // failures + duplicate invitation emails across N leads — a real rabbit hole,
  // so it's deliberately left out of bulk actions here.
  const getBulkActions = useCallback(
    (selectedRows: Row[]): ActionDropdownItem[] => [
      {
        label: `Export ${selectedRows.length} selected`,
        onClick: () => exportRowsToCsv(selectedRows.map(toCsvRow), 'leads'),
        icon: <DownloadIcon strokeWidth={1} />,
      },
      {
        label: `Copy ${selectedRows.length} email${selectedRows.length === 1 ? '' : 's'}`,
        onClick: () => {
          navigator.clipboard.writeText(selectedRows.map((row) => row.email).join(', '))
          notification({
            type: 'success',
            text: `Copied ${selectedRows.length} email${selectedRows.length === 1 ? '' : 's'} to clipboard`,
          })
        },
        icon: <CopyIcon strokeWidth={1} />,
      },
    ],
    [notification],
  )

  const columns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        pinned: 'left',
        disableReorder: true,
        width: 250,
        renderCell: (params: GridRenderCellParams<Row>) => <LeadNameCell row={params.row} />,
      },
      {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        width: 220,
        // VRG-720: the phone the lead shared sits under the email rather than taking a
        // column of its own — both are ways to reach the same person, so they are read
        // together, and a column per field is how a grid stops being scannable (§49).
        renderCell: ({ row }) => <TwoLineCell top={row.email} bottom={row.phone} />,
      },
      {
        field: 'company',
        headerName: 'Company',
        type: 'string',
        width: 200,
        // Not sortable, because the server sorts on lastSeen / firstSeen / scans / email
        // only. A sortable header the server cannot honour would re-order nothing and
        // read as a broken control.
        sortable: false,
        // Company over job title: one professional identity, and the title is only ever
        // meaningful next to the employer it belongs to.
        renderCell: ({ row }) =>
          row.company || row.title ? (
            <TwoLineCell
              top={row.company ?? row.title}
              bottom={row.company ? row.title : null}
              // Title first here, company second: that is how a person says it out loud,
              // and it avoids the em-dash separator §15 rules out of UI copy. The
              // stacked cell keeps the other order because the company is the scannable
              // half; the hover is a sentence, so it reads as one.
              title={[row.title, row.company].filter(Boolean).join(', ') || undefined}
            />
          ) : (
            '—'
          ),
      },
      {
        field: 'certificate',
        headerName: 'Certificate',
        type: 'string',
        width: 220,
        sortable: false,
        renderCell: ({ row }) =>
          row.certificate ? <CertificateCell certificate={row.certificate} size={40} /> : '—',
      },
      {
        field: 'referrers',
        headerName: 'Referrers',
        type: 'string',
        width: 160,
        sortable: false,
        renderCell: ({ row }) =>
          row.referrers.length > 0 ? (
            // VERA-914 (Stage B) — size 40 to match the row's CertificateCell avatar
            // (32 read small next to it). Stack wrapper guarantees vertical centering
            // regardless of the cell's own height, without touching the shared cell.
            <Stack height="100%" justifyContent="center">
              <UserGroupCell users={row.referrers} route={ROUTES.OWNER} size={40} align="left" />
            </Stack>
          ) : (
            '—'
          ),
      },
      {
        field: 'scans',
        headerName: 'Scans',
        type: 'number',
        width: 100,
      },
      {
        field: 'lastSeen',
        headerName: 'Last Scanned',
        type: 'dateTime',
        width: 160,
        valueGetter: (value: string) => value && new Date(value),
        // Relative for the last 24h ("3 hours ago"), absolute date once older — full
        // precision stays a hover away, same convention as the scan row's timestamp.
        renderCell: ({ row }) =>
          row.lastSeen ? (
            <Tooltip title={datetime(row.lastSeen)} placement="left" arrow>
              <span>{relativeOrDate(row.lastSeen)}</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        field: 'lastScanLocation',
        headerName: 'Last scan location',
        type: 'string',
        width: 180,
        sortable: false,
        // TODO(VERA-260 / TD-074): make this deep-link to that scan on the Analytics
        // page (scan map). Needs an Analytics focus-scan route + the last scan's id
        // returned by the leads read. Deferred to VERA-260.
        renderCell: ({ value }) => value || '—',
      },
      {
        field: 'actions',
        headerName: ' ',
        sortable: false,
        align: 'center',
        width: 54,
        pinned: 'right',
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
      },
    ],
    // `isCanTransfer` is false until the permission set resolves, and `getActionItems`
    // closes over it. Left at [], every row kebab would keep the pre-load value and
    // show Transfer permanently disabled to someone who holds the permission.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCanTransfer]
  )

  const getEmptyState = useMemo(() => {
    if (total === 0 && !search) {
      return (
        <EmptyState
          title="No leads yet"
          text="People who scan your certificates and share their email will appear here."
          icon={<UsersRoundIcon strokeWidth={1} size={32} />}
        />
      )
    }

    return (
      <EmptyState
        title="No leads found"
        text="No leads match the current search."
        icon={<UsersRoundIcon strokeWidth={1} size={32} />}
      />
    )
  }, [total, search])

  return (
    <>
    <Table
      columns={columns}
      rows={leads}
      getRowId={(row: Row) => row.email}
      loading={isLoading}
      fillBottom
      bulkActions={getBulkActions}
      alwaysShowRowSelection={false}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={setRowSelectionModel}
      showSearch={true}
      search={search}
      onSearchChange={(value) => {
        setSearch(value)
        setPaginationModel((prev) => ({ ...prev, page: 0 }))
      }}
      showFilters={true}
      onFiltersChange={handleFiltersChange}
      showSort={true}
      paginationMode="server"
      rowCount={total ?? -1}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      extraFilters={
        <Stack spacing={5}>
          <Stack spacing={2}>
            <Typography variant="body2">Referrer</Typography>
            <SelectField
              search
              placeholder="All owners"
              options={ownerOptions}
              value={referrerDraft ?? ''}
              onChange={(value) => setReferrerDraft((value as string) || undefined)}
            />
          </Stack>
          <Stack spacing={2}>
            <Typography variant="body2">Registered</Typography>
            <SelectField
              options={REGISTERED_OPTIONS}
              value={registeredDraft}
              onChange={(value) => setRegisteredDraft(value as RegisteredFilter)}
            />
          </Stack>
        </Stack>
      }
      extraFiltersDirty={extraFiltersDirty}
      onFiltersApply={handleFiltersApply}
      // Clear owns the extras too — reset the drafts AND the applied values, or the
      // next commit puts Referrer/Registered straight back. Referrer clears to
      // undefined rather than the `?referrer=` deep-link seed: the seed is an entry
      // condition, not a default the user asked to keep.
      onClearExtraFilters={() => {
        setReferrerDraft(undefined)
        setReferrerFilter(undefined)
        setRegisteredDraft('all')
        setRegistered('all')
        setPaginationModel((prev) => ({ ...prev, page: 0 }))
      }}
      emptyState={getEmptyState}
      viewKey="leads"
    />
      {/**
        * Mounted only while a lead is selected, rather than always-on with `open`.
        * The modal fetches the restricted certificate set on mount, and that fetch is
        * keyed on THIS lead's scanned ids — keeping a single instance alive across
        * leads would carry the previous lead's resolved certificate into the next
        * one's modal, which is the one mistake this action cannot make.
        */}
      {transferLead && (
        <TransferCertificateModal
          open
          restrictToCertificateIds={transferLead.scannedCertificateIds}
          // The recipient is the lead's email, not an owner on file. The modal renders
          // it as an INVITE rather than an owner card, and the server provisions the
          // owner on submit — which is the whole reason this is one action.
          inviteEmail={transferLead.email}
          onClose={() => setTransferLead(null)}
          refetch={() => {
            // The lead becomes an owner, so they leave this list. Refetch rather than
            // patching the row: whether they drop out depends on a server-side
            // not-an-owner check this component does not evaluate.
            void refetchLeads()
          }}
        />
      )}
    </>
  )
})

LeadsTab.displayName = 'LeadsTab'
